import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useLocation } from "react-router-dom";

import { DATE_FORMATS_FNS, EllipsisText, ReactTable } from "components";
import { PATHS } from "router/paths";
import { formatDate } from "utils/dateHelperFunctions";

import type { Car, DealerLocation } from "models";

type CustomerCarsTableProps = {
  cars?: Car[];
  location?: DealerLocation | null;
};

const columnHelper = createColumnHelper<Car>();

export const CustomerCarsTable = ({ cars, location }: CustomerCarsTableProps) => {
  const { t } = useTranslation();
  const routerLocation = useLocation();

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "makeModel",
        header: t("v8_make_model", "Make and model"),
        cell: ({ row: { original } }) => <EllipsisText text={`${original.make} ${original.model}`} />,
        size: 200
      }),
      columnHelper.accessor(row => row, {
        id: "fuel",
        header: t("v8_fuel", "Fuel"),
        cell: ({ row: { original } }) => <EllipsisText text={String(original.fuel)} />,
        size: 200
      }),
      columnHelper.accessor(row => row, {
        id: "dms_nr",
        header: t("v8_dms_nr", "DMS"),
        cell: ({ row: { original } }) => <EllipsisText text={String(original.dms_nr)} />,
        size: 200
      }),
      columnHelper.accessor(row => row, {
        id: "reg_nr",
        header: t("v8_reg_nr", "Registration"),
        cell: ({ row: { original } }) => <EllipsisText text={String(original.reg_nr)} />,
        size: 200
      }),
      columnHelper.accessor(row => row, {
        id: "vin_nr",
        header: t("v8_vin_nr", "VIN"),
        cell: ({ row: { original } }) => <EllipsisText text={String(original.vin_nr)} />,
        size: 200
      }),
      columnHelper.accessor(row => row, {
        id: "apkDate",
        header: t("v8_apk_date", "APK date"),
        cell: ({ row: { original } }) => <span>{original.apk_date_dms ? formatDate(original.apk_date_dms, DATE_FORMATS_FNS.dateMonthYear) : ""}</span>,
        size: 300
      }),
      columnHelper.accessor(row => row, {
        id: "huDateDms",
        header: t("v8_hu_date_dms", "HU date dms"),
        cell: ({ row: { original } }) => <span>{original.hu_date_dms ? formatDate(original.hu_date_dms, DATE_FORMATS_FNS.dateMonthYear) : ""}</span>,
        size: 300
      })
    ],
    [t]
  );

  if (!cars?.length) return <p className="no-results">{t("v8_no_cars", "No cars")}</p>;

  const handleRowClick = ({ id }: Car) => window.open(`${generatePath(PATHS.CAR_DETAILS, { id })}?from=${routerLocation.pathname}`, "_blank");

  return <ReactTable columns={columns} data={cars} onRowClick={handleRowClick} state={{ columnVisibility: { huDateDms: Boolean(location?.hu_visible) } }} />;
};
